import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import GameCard from '../../components/GameCard';
import { BRANDING } from '../../config/branding';
import { SOCIAL_LINKS, SPORTS, SPORT_ICON_MAP } from '../../utils/constants';
import MergedExampleGameData from '../../utils/mock_data/mergedExampleData';
import {
  ActionBtn,
  BarFill,
  BarTrack,
  Bullets,
  CardIcon,
  CenterActions,
  ConfCard,
  ConfNote,
  DCDesc,
  DCHeading,
  DCCardsRow,
  DCInner,
  DCSection,
  DCSportTab,
  DCSportsNavWrap,
  DCTopLine,
  DocActions,
  DocCard,
  DocGrid,
  FaqItem,
  FeatBox,
  GlowOrb,
  HeroBadge,
  HeroBtns,
  HeroContent,
  HeroLogoWrap,
  HeroSub,
  HeroWrap,
  IconLeading,
  LiveDot,
  PCHead,
  PBar,
  PrimaryBtn,
  PropsCard,
  PropsLayout,
  PropItem,
  ScrollInd,
  Sec,
  SecDesc,
  SecH2,
  SecInner,
  SecondaryBtn,
  TopLine,
  TrustDiv,
  TrustRow,
  TrustStat,
  TwoCol,
  BottomCtaInner,
  BottomTitle,
  BottomDesc,
} from './styles';

const FAQ_DATA = [
  {
    q: 'How should I read a game card from top to bottom?',
    a: 'Start in Prediction to see projected score and winner direction, then check Proven Line and Lines for market context, then validate with Factors, Injuries, Roster, and Schedule windows before making a decision.',
  },
  {
    q: 'What does the PW Rating actually mean?',
    a: 'PW Rating is a trust signal, not a guarantee. It reflects how well the model has historically performed in similar matchup environments. Use a higher PW Rating to prioritize spots, then confirm with line value and context windows.',
  },
  {
    q: 'What is Proven Line and how is it different from sportsbook odds?',
    a: 'Proven Line is the platform\'s model-driven pricing view of a matchup. Sportsbook odds are market numbers. The edge comes from comparing model view versus market view, then checking whether PW Rating and context agree.',
  },
  {
    q: 'How does access work now?',
    a: 'Paid plans unlock all sports. Predictions are credit-based: each game unlock costs 1 credit, or 3 credits when the PW Rating is 70% or higher. Props data is available as a separate monthly add-on.',
  },
  {
    q: 'I tapped a package on mobile. What should I do next?',
    a: 'After selecting a package, go straight to Step 2 on the page and complete the checkout form. Once payment succeeds in Stripe, your account and access are provisioned automatically.',
  },
  {
    q: 'Why do the model and market sometimes disagree?',
    a: 'That is expected. Market movement can be driven by public behavior, timing, and news flow. Disagreement is where value may exist, but only act when multiple windows still support the same direction.',
  },
];

const Home = () => {
  const history = useHistory();
  const [sport, setSport] = useState('mlb');
  const [openFaq, setOpenFaq] = useState(null);

  const card = MergedExampleGameData[sport];

  return (
    <>
      <Helmet>
        <title>Home | {BRANDING.appName}</title>
        <meta name="description" content={BRANDING.seo.defaultDescription} />
        <meta property="og:title" content={`Home | ${BRANDING.appName}`} />
        <meta property="og:description" content={BRANDING.seo.defaultDescription} />
        <meta property="og:image" content={BRANDING.seo.ogImage} />
        <link rel="canonical" href={BRANDING.seo.siteUrl} />
      </Helmet>

      <HeroWrap>
        <GlowOrb className="orb-1" />
        <GlowOrb className="orb-2" />
        <HeroContent>
          <HeroLogoWrap>
            <img src={BRANDING.logoPath} alt={BRANDING.appName} />
          </HeroLogoWrap>
          <HeroBadge>
            <LiveDot /> Live Predictions Active
          </HeroBadge>
          <HeroSub>Backed by Data. Powered by Algorithms.</HeroSub>
          <HeroBtns>
            <PrimaryBtn onClick={() => history.push('/packages')}>
              <IconLeading className="fas fa-user-plus" />Get Access
            </PrimaryBtn>
            <SecondaryBtn onClick={() => history.push('/packages')}>
              <IconLeading className="fas fa-crown" />View Packages
            </SecondaryBtn>
          </HeroBtns>
          <TrustRow>
            <TrustStat>
              <span className="value">Game Cards</span>
              <span className="label">Daily matchup board</span>
            </TrustStat>
            <TrustDiv />
            <TrustStat>
              <span className="value">PW Rating</span>
              <span className="label">ML trust signal</span>
            </TrustStat>
            <TrustDiv />
            <TrustStat>
              <span className="value">Proven Line</span>
              <span className="label">Favorite pricing</span>
            </TrustStat>
            <TrustDiv />
            <TrustStat>
              <span className="value">Props</span>
              <span className="label">Included with paid access</span>
            </TrustStat>
          </TrustRow>
        </HeroContent>
        <ScrollInd>
          <i className="fas fa-chevron-down" />
        </ScrollInd>
      </HeroWrap>

      <DCSection>
        <DCInner>
          <DCTopLine>Why Choose {BRANDING.appName}?</DCTopLine>
          <DCHeading>Data-Driven Insights &amp; Predictive Analytics</DCHeading>
          <DCDesc>
            Harness the power of advanced algorithms and comprehensive data analysis. Our platform combines
            proprietary power rankings with machine learning predictions to give you a complete edge on every matchup.
          </DCDesc>
          <DCSportsNavWrap>
            {SPORTS.map(s => {
              return (
                <DCSportTab
                  key={s}
                  $active={sport === s}
                  onClick={() => setSport(s)}
                  title={s.toUpperCase()}
                >
                  <i className={`fas ${SPORT_ICON_MAP[s]}`} /> {s.toUpperCase()}
                </DCSportTab>
              );
            })}
          </DCSportsNavWrap>
          <DCCardsRow>
            <GameCard game={card} sport={sport} defaultWindow="power-rank" demo />
            <GameCard game={card} sport={sport} defaultWindow="prediction" demo />
          </DCCardsRow>
          <CenterActions>
            <ActionBtn onClick={() => history.push('/packages')}>
              <IconLeading className="fas fa-rocket" />Get Started Today
            </ActionBtn>
          </CenterActions>
        </DCInner>
      </DCSection>

      <Sec bg="linear-gradient(145deg, #090909 0%, #16100a 50%, #090909 100%)">
        <SecInner>
          <TopLine light>PW Rating System</TopLine>
          <SecH2 light>Machine-Learned PW Rating, Not Just Guesswork</SecH2>
          <SecDesc light>Every prediction we make is rated with a PW Rating using a custom machine learning system.</SecDesc>
          <TwoCol>
            <div>
              <FeatBox>
                <div className="ico">
                  <i className="fas fa-brain" />
                </div>
                <div>
                  <h5>Trained on Years of Data</h5>
                  <p>Our model evaluates predictions against a massive dataset of historical games, adjusting for context like injuries, home/away splits, and pace.</p>
                </div>
              </FeatBox>
              <FeatBox>
                <div className="ico">
                  <i className="fas fa-project-diagram" />
                </div>
                <div>
                  <h5>Similarity Scoring</h5>
                  <p>We find games with similar matchups and team profiles to assess how accurate the model was in comparable scenarios.</p>
                </div>
              </FeatBox>
              <FeatBox>
                <div className="ico">
                  <i className="fas fa-chart-line" />
                </div>
                <div>
                  <h5>PW Rating Output</h5>
                  <p>Each prediction is paired with a 0-100 PW Rating. Higher scores mean more historical precedent supports the model's current outlook.</p>
                </div>
              </FeatBox>
            </div>
            <ConfCard>
              <h6>Example: PW Rating</h6>
              <BarTrack>
                <BarFill $w="84%">84% PW Rating</BarFill>
              </BarTrack>
              <BarTrack>
                <BarFill $w="71%">71% Spread PW Rating</BarFill>
              </BarTrack>
              <ConfNote>
                This pick matches 17 historically similar games where the model was correct 84% of the time.
                PW Rating helps you weigh the model's trust - not just the prediction.
              </ConfNote>
            </ConfCard>
          </TwoCol>
        </SecInner>
      </Sec>

      <Sec bg="#f7f9fc">
        <SecInner>
          <PropsLayout>
            <div>
              <TopLine>Player Prop Insights</TopLine>
              <SecH2>View player prop data across every sport.</SecH2>
              <SecDesc>Historical prop outputs, player usage metrics, and matchup context in one place so you can evaluate every angle faster.  Get player props for all of your sports seamlessly.</SecDesc>
              <Bullets>
                <div className="b">
                  <i className="fas fa-check-circle" /> Last-game and recent-game results for each prop line
                </div>
                <div className="b">
                  <i className="fas fa-check-circle" /> Player stats, minutes, and usage trend breakdowns
                </div>
                <div className="b">
                  <i className="fas fa-check-circle" /> Line movement context and matchup-driven splits
                </div>
              </Bullets>
              <ActionBtn onClick={() => history.push('/packages')}>
                <IconLeading className="fas fa-rocket" />Get Access
              </ActionBtn>
            </div>
            <PropsCard>
              <PCHead>
                <span className="t">Prop Finder Snapshot</span>
                <span className="s">Updated hourly</span>
              </PCHead>
              <PropItem>
                <div className="rh">
                  <span className="n">Nikola Jokic Rebounds</span>
                  <span className="l">O/U 11.5</span>
                </div>
                <div className="m">
                  <span className="e">Last 5: 14, 12, 11, 15, 13</span>
                  <span className="c">Minutes avg: 37.2</span>
                </div>
                <PBar $w="87%">
                  <div />
                </PBar>
              </PropItem>
              <PropItem>
                <div className="rh">
                  <span className="n">Jayson Tatum Points</span>
                  <span className="l">O/U 28.5</span>
                </div>
                <div className="m">
                  <span className="e">Last 5: 31, 27, 29, 33, 26</span>
                  <span className="c">Usage: 31%</span>
                </div>
                <PBar $w="74%">
                  <div />
                </PBar>
              </PropItem>
            </PropsCard>
          </PropsLayout>
        </SecInner>
      </Sec>

      <Sec bg="linear-gradient(145deg, #090909 0%, #16100a 50%, #090909 100%)">
        <SecInner>
          <TopLine light>System Docs</TopLine>
          <SecH2 light>Understand the system before you place your plays.</SecH2>
          <SecDesc light>
            The docs page walks through how predictions, PW Rating, and multi-window research work together in the app.
          </SecDesc>
          <DocGrid>
            <DocCard>
              <h4>
                <CardIcon className="fas fa-lightbulb" />Understanding The System
              </h4>
              <p>Learn what each major game-card signal means and how to evaluate model output in context.</p>
            </DocCard>
            <DocCard>
              <h4>
                <CardIcon className="fas fa-brain" />PW Rating Workflow
              </h4>
              <p>Use PW Rating as a prioritization signal, then verify with lines, injuries, trends, and schedule windows.</p>
            </DocCard>
          </DocGrid>
          <DocActions>
            <ActionBtn onClick={() => history.push('/system-docs')}>
              <IconLeading className="fas fa-book-open" />Open System Docs
            </ActionBtn>
          </DocActions>
        </SecInner>
      </Sec>

      <Sec bg="#f7f9fc">
        <SecInner>
          <TopLine>Follow Us</TopLine>
          <SecH2>Get daily edges and updates on social.</SecH2>
          <SecDesc>
            Follow Proven Winner for picks context, model notes, and product updates across X, Instagram, and Winible.
          </SecDesc>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid #e2e8f0', borderRadius: 20, padding: '2.5rem 2rem', background: '#ffffff', textAlign: 'center' }}>
              <i className="fab fa-x-twitter" style={{ fontSize: '3.5rem', color: '#000000' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#000000', fontSize: '1.15rem' }}>X (Twitter)</h4>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: 1.55 }}>Get the latest picks context and model notes from Proven Winner.</p>
              <a href={SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: '#000000', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(0,0,0,.22)', marginTop: '0.25rem' }}>
                <i className="fab fa-x-twitter" /> Follow on X
              </a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid #e2e8f0', borderRadius: 20, padding: '2.5rem 2rem', background: '#ffffff', textAlign: 'center' }}>
              <i className="fab fa-instagram" style={{ fontSize: '3.5rem', color: '#E4405F' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#E4405F', fontSize: '1.15rem' }}>Instagram</h4>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: 1.55 }}>Behind-the-scenes content, updates, and daily card highlights.</p>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(228,64,95,.32)', marginTop: '0.25rem' }}>
                <i className="fab fa-instagram" /> Follow on Instagram
              </a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid #e2e8f0', borderRadius: 20, padding: '2.5rem 2rem', background: '#ffffff', textAlign: 'center' }}>
              <i className="fas fa-chart-line" style={{ fontSize: '3.5rem', color: '#16a34a' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#16a34a', fontSize: '1.15rem' }}>Winible</h4>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: 1.55 }}>Track our documented pick record and betting history on Winible.</p>
              <a href={SOCIAL_LINKS.winible} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: '#16a34a', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(22,163,74,.3)', marginTop: '0.25rem' }}>
                <i className="fas fa-chart-line" /> Follow on Winible
              </a>
            </div>
          </div>
        </SecInner>
      </Sec>

      <Sec bg="linear-gradient(145deg, #090909 0%, #16100a 50%, #090909 100%)">
        <SecInner>
          <TopLine light>FAQ</TopLine>
          <SecH2 light>Frequently Asked Questions</SecH2>
          <SecDesc light>Practical answers on predictions, PW Rating, market context, packages, and checkout flow.</SecDesc>
          {FAQ_DATA.map((item, i) => (
            <FaqItem key={i} $open={openFaq === i}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {item.q}
                <i className={`fas fa-chevron-${openFaq === i ? 'up' : 'down'}`} />
              </button>
              <div className="ans">{item.a}</div>
            </FaqItem>
          ))}
        </SecInner>
      </Sec>

      <Sec bg="#f7f9fc">
        <BottomCtaInner>
          <TopLine>Get Started</TopLine>
          <BottomTitle>Ready to gain the edge?</BottomTitle>
          <BottomDesc>Choose your access package, log in, and unlock the full game-card board.</BottomDesc>
          <ActionBtn onClick={() => history.push('/packages')}>
            <IconLeading className="fas fa-sign-in-alt" />Get Started
          </ActionBtn>
        </BottomCtaInner>
      </Sec>
    </>
  );
};

export default Home;
